import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import StepIndicator from '@/components/create/StepIndicator'
import PromptInput from '@/components/create/PromptInput'
import TemplateSelector from '@/components/create/TemplateSelector'
import VoiceSettings from '@/components/create/VoiceSettings'
import PublishSettings from '@/components/create/PublishSettings'
import { PlusCircle, Settings, ArrowLeft, ArrowRight, Wand2, Sparkles, RefreshCw } from 'lucide-react'
import CreationProgress from '@/components/create/CreationProgress'
import CreationSettings from '@/components/create/CreationSettings'
import { API } from '@/api'
import { CreationTask, Story, StoryConfiguration } from '@/types'
import { useToast } from '@/components/ui/use-toast'

type CreationStep = 'prompt' | 'template' | 'voice' | 'settings'
type CreationStatus = 'idle' | 'generating' | 'completed' | 'failed'

const Create = () => {
  const [mode, setMode] = useState<'quick' | 'pro'>('quick')
  const [currentStep, setCurrentStep] = useState<CreationStep>('prompt')
  const [prompt, setPrompt] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const [creationStatus, setCreationStatus] = useState<CreationStatus>('idle')
  const [progress, setProgress] = useState(0)
  const [currentGenerationStep, setCurrentGenerationStep] = useState('')
  const [showPublish, setShowPublish] = useState(false)
  const [currentTask, setCurrentTask] = useState<CreationTask | null>(null)
  const [currentStory, setCurrentStory] = useState<Story | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { toast } = useToast()

  const steps = [
    {
      title: '创作提示',
      description: '描述故事内容',
      status: currentStep === 'prompt' 
        ? 'current' 
        : currentStep === 'template' || currentStep === 'voice' || currentStep === 'settings' 
          ? 'completed' 
          : 'upcoming'
    },
    {
      title: '选择模板',
      description: '设置绘本风格',
      status: currentStep === 'template' 
        ? 'current' 
        : currentStep === 'voice' || currentStep === 'settings'
          ? 'completed' 
          : 'upcoming'
    },
    {
      title: '配音设置',
      description: '添加语音效果',
      status: currentStep === 'voice' 
        ? 'current' 
        : currentStep === 'settings'
          ? 'completed' 
          : 'upcoming'
    },
    {
      title: '创作设置',
      description: '设置内容形式',
      status: currentStep === 'settings' ? 'current' : 'upcoming'
    }
  ] as const

  const createNewStory = async (title: string) => {
    try {
      const story = await API.stories.create({
        title,
        description: prompt,
        category: ''
      })
      setCurrentStory(story)
      return story
    } catch (error) {
      console.error('Failed to create story:', error)
      throw error
    }
  }

  const startCreationTask = async (storyId: string) => {
    try {
      const config: Partial<StoryConfiguration> = {
        prompt,
        templateId: selectedTemplate || undefined,
        style: 'watercolor',
        theme: 'fantasy',
        pageCount: 5,
        format: 'horizontal',
        ageGroup: '3-6',
        hasImage: true,
        hasText: true,
        hasVoice: false
      }

      const task = await API.creation.startCreation({
        storyId,
        prompt,
        config
      })
      setCurrentTask(task)
      return task
    } catch (error) {
      console.error('Failed to start creation task:', error)
      throw error
    }
  }

  const pollTaskStatus = async (taskId: string) => {
    try {
      const task = await API.creation.getTaskStatus(taskId)
      setCurrentTask(task)
      setProgress(task.progress)
      setCurrentGenerationStep(`正在生成第 ${Math.ceil(task.progress / 20)} 页...`)

      if (task.status === 'COMPLETED') {
        setCreationStatus('completed')
      } else if (task.status === 'FAILED') {
        setCreationStatus('failed')
      } else {
        setTimeout(() => pollTaskStatus(taskId), 1000)
      }
    } catch (error) {
      console.error('Failed to get task status:', error)
      setCreationStatus('failed')
    }
  }

  const handleError = (error: any, message: string) => {
    console.error(error)
    toast({
      title: '错误',
      description: message,
      variant: 'destructive'
    })
  }

  const simulateCreation = async () => {
    setIsLoading(true)
    setCreationStatus('generating')
    try {
      const story = await createNewStory(prompt)
      const task = await startCreationTask(story.id)
      await pollTaskStatus(task.id)
    } catch (error) {
      handleError(error, '创作过程中出现错误')
      setCreationStatus('failed')
    } finally {
      setIsLoading(false)
    }
  }

  const saveStoryContent = async (pageNumber: number, content: string, imageUrl: string) => {
    if (!currentStory) return

    try {
      await API.creation.saveContent({
        storyId: currentStory.id,
        pageNumber,
        textContent: content,
        imageUrl,
        audioUrl: undefined
      })
    } catch (error) {
      console.error('Failed to save content:', error)
    }
  }

  const handlePublish = async () => {
    if (!currentStory) return
    
    try {
      const updatedStory = await API.stories.update(currentStory.id, {
        status: 'published',
        visibility: 'public'
      })
      setCurrentStory(updatedStory)
      // TODO: 跳转到故事详情页
    } catch (error) {
      console.error('Failed to publish story:', error)
    }
  }

  const handleSaveDraft = async () => {
    if (!currentStory) return
    
    try {
      const updatedStory = await API.stories.update(currentStory.id, {
        status: 'draft'
      })
      setCurrentStory(updatedStory)
      // TODO: 跳转到我的作品页
    } catch (error) {
      console.error('Failed to save draft:', error)
    }
  }

  const renderQuickMode = () => {
    const handleQuickCreate = async () => {
      if (!prompt) return
      await simulateCreation()
    }

    return (
      <div className="max-w-4xl mx-auto py-8">
        <Card className="p-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-2">
                <div className="p-3 bg-primary/10 rounded-xl">
                  <Wand2 className="w-5 h-5 text-primary" />
                </div>
                <h2 className="text-lg font-bold text-gray-800">快速创作</h2>
              </div>
              <Button
                variant="ghost"
                onClick={() => setMode('pro')}
                className="flex items-center gap-2 hover:bg-secondary/10 transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span>高级创作</span>
              </Button>
            </div>

            <div className="space-y-6">
              <div className="flex items-center">
                <div className="flex items-center gap-2 mr-4">
                  <Sparkles className="w-3.5 h-3.5 text-primary/70" />
                  <p className="text-sm font-medium text-gray-600">选择这些主题快速开始：</p>
                </div>
                <div className="flex gap-3 overflow-x-auto py-2">
                  {['放大镜下的森林', '小红猫的冒险日记', '草原上的音乐会'].map((template) => (
                    <Button
                      key={template}
                      variant="ghost"
                      className="h-8 px-4 text-sm text-gray-500 hover:text-primary whitespace-nowrap bg-primary/5 hover:bg-primary/10 shadow-sm hover:shadow-md transition-all"
                      onClick={() => setPrompt(template)}
                    >
                      {template}
                    </Button>
                  ))}
                  <Button
                    variant="ghost"
                    className="h-8 px-3 text-sm text-gray-500 whitespace-nowrap flex items-center gap-1.5 hover:bg-secondary/5 transition-colors"
                    onClick={() => setPrompt('')}
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    重新输入
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 hover:border-primary/20 transition-colors">
                <div className="w-full">
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="请输入您想生成的内容，不超过50字"
                    className="w-full h-32 p-4 rounded-xl border-0 bg-transparent resize-none focus:outline-none focus:ring-0"
                    maxLength={50}
                  />
                  <div className="text-right text-sm text-gray-400 mt-2">
                    {prompt.length}/50
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  className="flex items-center gap-2 bg-gradient-to-r from-primary via-accent to-secondary hover:opacity-90 px-8"
                  onClick={handleQuickCreate}
                >
                  <PlusCircle className="w-4 h-4" />
                  <span>开始创作</span>
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  const handleSkipVoice = () => {
    setCurrentStep('preview')
  }

  const renderProMode = () => {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <Card className="p-8">
          <div className="flex items-start gap-8 mb-6">
            <Button
              variant="ghost"
              onClick={() => setMode('quick')}
              className="flex items-center gap-2 shrink-0 hover:bg-primary/10 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回快速创作</span>
            </Button>

            <div className="flex-1">
              <StepIndicator 
                steps={steps}
                currentStep={steps.findIndex(s => s.status === 'current')}
              />
            </div>
          </div>
          
          <div className="mt-8">
            {currentStep === 'prompt' && (
              <PromptInput
                value={prompt}
                onChange={setPrompt}
                onNext={handleNext}
                showPrevious={false}
              />
            )}
            {currentStep === 'template' && (
              <TemplateSelector
                selectedTemplate={selectedTemplate}
                onSelect={(template) => setSelectedTemplate(template)}
                onNext={handleNext}
                onPrevious={handlePrevious}
              />
            )}
            {currentStep === 'voice' && (
              <VoiceSettings
                onNext={handleNext}
                onPrevious={handlePrevious}
                onSkip={() => setCurrentStep('settings')}
              />
            )}
            {currentStep === 'settings' && (
              <CreationSettings
                onNext={() => simulateCreation()}
                onPrevious={handlePrevious}
              />
            )}
          </div>
        </Card>
      </div>
    )
  }

  const handleNext = () => {
    switch (currentStep) {
      case 'prompt':
        setCurrentStep('template')
        break
      case 'template':
        if (selectedTemplate) {
          setCurrentStep('voice')
        }
        break
      case 'voice':
        setCurrentStep('settings')
        break
      case 'settings':
        simulateCreation()
        break
      default:
        break
    }
  }

  const handlePrevious = () => {
    switch (currentStep) {
      case 'template':
        setCurrentStep('prompt')
        break
      case 'voice':
        setCurrentStep('template')
        break
      case 'settings':
        setCurrentStep('voice')
        break
      default:
        break
    }
  }

  const handleContinueToPublish = () => {
    setCreationStatus('idle')
    setShowPublish(true)
  }

  const handleRetryCreation = () => {
    setCreationStatus('idle')  // 重置状态
    simulateCreation()        // 重新开始创作
  }

  const renderContent = () => {
    if (showPublish) {
      return (
        <PublishSettings
          onPublish={handlePublish}
          onSaveDraft={handleSaveDraft}
        />
      )
    }

    if (creationStatus === 'generating' || creationStatus === 'completed' || creationStatus === 'failed') {
      return (
        <CreationProgress 
          status={creationStatus}
          progress={progress}
          currentStep={currentGenerationStep}
          onContinue={creationStatus === 'completed' ? handleContinueToPublish : undefined}
          onRetry={creationStatus === 'failed' ? handleRetryCreation : undefined}
        />
      )
    }

    return mode === 'quick' ? renderQuickMode() : renderProMode()
  }

  // 添加重置状态的函数
  const resetState = () => {
    setMode('quick')
    setCurrentStep('prompt')
    setPrompt('')
    setSelectedTemplate(null)
    setCreationStatus('idle')
    setProgress(0)
    setCurrentGenerationStep('')
    setShowPublish(false)
    setCurrentTask(null)
    setCurrentStory(null)
  }

  // 在组件挂载时重置状态
  useEffect(() => {
    resetState()
  }, []) // 空依赖数组表示只在组件挂载时执行

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {isLoading && <div>加载中...</div>}
      {renderContent()}
    </div>
  )
}

export default Create 